import * as React from 'react';
import { observer } from 'mobx-react';
import { trace } from 'owa-trace';

export interface ErrorComponentProps {
    error: Error;
}

export enum ErrorComponentType {
    Full,
    Simple,
    None,
}

export interface ErrorBoundaryProps {
    child: JSX.Element;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    type?: ErrorComponentType;
    fullErrorComponent?: React.ComponentType<{ error: Error }>;
    suppressErrorReport?: boolean;
}

export interface ErrorBoundaryState {
    error?: Error;
}

@observer
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.props.onError?.(error, errorInfo);

        if (!this.props.suppressErrorReport) {
            trace.error({
                component: true,
                error: error,
                diagnosticInfo: errorInfo.componentStack,
            });
        }

        this.setState({
            error: error,
        });
    }

    render() {
        const { error } = this.state;
        const { child, type } = this.props;

        if (error) {
            if (type == ErrorComponentType.None) {
                return null;
            } else if (type == ErrorComponentType.Simple || !this.props.fullErrorComponent) {
                return <div>{error}</div>;
            }

            const FullErrorComponent = this.props.fullErrorComponent;

            return <FullErrorComponent error={error} />;
        } else {
            // React 15 doesn't support returning arrays from render, so in order to be backward
            // compatible we can't just return this.props.children.  Once we're fully on React 16
            // we can get rid of the 'child' prop and just use 'children'.
            return child;
        }
    }
}
